
import React from 'react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './tabs';
import type { TabsProps } from '@radix-ui/react-tabs';

// Create explicit interface that extends TabsProps but with correct dir type
interface TabsWithChildrenProps extends Omit<React.ComponentPropsWithoutRef<typeof Tabs>, 'dir'> {
  children: React.ReactNode;
  dir?: 'ltr' | 'rtl'; // Make dir match the expected type
}

export const TabsWithChildren = React.forwardRef<
  HTMLDivElement,
  TabsWithChildrenProps
>(({ className, children, ...props }, ref) => (
  <Tabs ref={ref} className={cn(className)} {...props}>
    {children}
  </Tabs>
));

TabsWithChildren.displayName = 'TabsWithChildren';

interface TabsListWithChildrenProps extends React.ComponentPropsWithoutRef<typeof TabsList> {
  children: React.ReactNode;
}

export const TabsListWithChildren = React.forwardRef<
  HTMLDivElement,
  TabsListWithChildrenProps
>(({ className, children, ...props }, ref) => (
  <TabsList ref={ref} className={cn(className)} {...props}>
    {children}
  </TabsList>
));

TabsListWithChildren.displayName = 'TabsListWithChildren';

interface TabsTriggerWithChildrenProps extends React.ComponentPropsWithoutRef<typeof TabsTrigger> {
  children: React.ReactNode;
}

export const TabsTriggerWithChildren = React.forwardRef<
  HTMLButtonElement,
  TabsTriggerWithChildrenProps
>(({ className, children, ...props }, ref) => (
  <TabsTrigger ref={ref} className={cn(className)} {...props}>
    {children}
  </TabsTrigger>
));

TabsTriggerWithChildren.displayName = 'TabsTriggerWithChildren';

interface TabsContentWithChildrenProps extends React.ComponentPropsWithoutRef<typeof TabsContent> {
  children: React.ReactNode;
}

export const TabsContentWithChildren = React.forwardRef<
  HTMLDivElement,
  TabsContentWithChildrenProps
>(({ className, children, ...props }, ref) => (
  <TabsContent ref={ref} className={cn(className)} {...props}>
    {children}
  </TabsContent>
));

TabsContentWithChildren.displayName = 'TabsContentWithChildren';

// Re-export other tabs components
export { TabsTrigger, TabsContent };
